import { diffConditionClearOps, diffConditionOps, diffDefinitionsToOps, prepareQuestionForAdd } from "../../services/terrain/templateSync.js";

const emptyDefinition = () => ({
  schemaVersion: 1,
  title: "",
  description: "",
  sections: [],
  reviewSections: [],
});

// toLocalDefinition projects the server's TemplateDefinitionDTO (schemaVersion,
// title, description, sections, reviewSections) into the shape the builder
// already understands. The server response is used verbatim otherwise, since
// its ids ARE the server ids.
function toLocalDefinition(draftDefinition) {
  return {
    schemaVersion: (draftDefinition && draftDefinition.schemaVersion) || 1,
    title: (draftDefinition && draftDefinition.title) || "",
    description: (draftDefinition && draftDefinition.description) || "",
    sections: (draftDefinition && draftDefinition.sections) || [],
    reviewSections: (draftDefinition && draftDefinition.reviewSections) || [],
  };
}

export default {
  namespaced: true,

  state: () => ({
    capabilities: null,
    builderDraft: emptyDefinition(),
    activeTemplateId: null,
    builderDirty: false,
    remoteDraft: null,
    autosaveStatus: 'idle',

    // Server-side sync state for the template builder (Requirements 2.1, 2.2, 2.6, 2.7).
    serverTemplateId: null,
    serverResourceVersion: 0,
    serverDefinition: null,
    serverLatestPublishedVersion: 0,
    serverInUse: false,
    // serverArchived: la plantilla ya venía marcada "Eliminada" (archived)
    // por el backend al cargarla — el badge del listado (TerrainTemplateList.vue)
    // ya la mostraba así; sin este campo el builder no tenía forma de saberlo.
    serverArchived: false,
    localToServerSectionIds: {},
    localToServerQuestionIds: {},
    saveStatus: 'idle', // 'idle' | 'saving' | 'saved' | 'error'
    saveErrorMessage: '',

    // Validación/publicación del borrador (tarea Fase 4, PB-24).
    validateStatus: 'idle', // 'idle' | 'validating' | 'valid' | 'invalid' | 'error'
    validationErrors: [],
    publishStatus: 'idle', // 'idle' | 'publishing' | 'published' | 'error'
    publishErrorMessage: '',
  }),

  getters: {
    builderDraft: (state) => state.builderDraft,
    sections: (state) => state.builderDraft.sections,
    isBuilderDirty: (state) => state.builderDirty,
    hasUnsavedChanges: (state) => state.builderDirty,
    saveStatus: (state) => state.saveStatus,
    saveErrorMessage: (state) => state.saveErrorMessage,
    serverTemplateId: (state) => state.serverTemplateId,
    latestPublishedVersion: (state) => state.serverLatestPublishedVersion,
    inUse: (state) => state.serverInUse,
    archived: (state) => state.serverArchived,
    validateStatus: (state) => state.validateStatus,
    validationErrors: (state) => state.validationErrors,
    publishStatus: (state) => state.publishStatus,
    publishErrorMessage: (state) => state.publishErrorMessage,
  },

  mutations: {
    SET_BUILDER_DRAFT(state, definition) {
      state.builderDraft = definition || emptyDefinition();
    },
    SET_ACTIVE_TEMPLATE_ID(state, templateId) {
      state.activeTemplateId = templateId || null;
    },
    SET_CAPABILITIES(state, capabilities) {
      state.capabilities = capabilities || null;
    },
    MARK_BUILDER_DIRTY(state, dirty) {
      state.builderDirty = !!dirty;
    },
    SET_AUTOSAVE_STATE(state, { status, draft = null }) {
      state.autosaveStatus = status || 'idle';
      if (draft) state.remoteDraft = draft;
    },
    RESET_AUTOSAVE(state) {
      state.remoteDraft = null;
      state.autosaveStatus = 'idle';
    },
    // RESET_BUILDER limpia también el estado ligado al servidor (id,
    // resourceVersion, definición, mapas de ids), no solo el draft local:
    // sin esto, navegar de "editar plantilla A" a "nueva plantilla" en la
    // misma sesión de SPA (sin recarga completa) arrastraría el
    // serverTemplateId de A, y el primer guardado de la "nueva" plantilla
    // aplicaría PUT sobre A en vez de crear una plantilla de verdad.
    RESET_BUILDER(state) {
      state.builderDraft = emptyDefinition();
      state.activeTemplateId = null;
      state.builderDirty = false;
      state.serverTemplateId = null;
      state.serverResourceVersion = 0;
      state.serverDefinition = null;
      state.serverLatestPublishedVersion = 0;
      state.serverInUse = false;
      state.serverArchived = false;
      state.localToServerSectionIds = {};
      state.localToServerQuestionIds = {};
      state.saveStatus = 'idle';
      state.saveErrorMessage = '';
      state.validateStatus = 'idle';
      state.validationErrors = [];
      state.publishStatus = 'idle';
      state.publishErrorMessage = '';
    },
    SET_SERVER_TEMPLATE(state, { templateId, resourceVersion, definition, latestPublishedVersion = 0, archived = false, inUse = false }) {
      state.serverTemplateId = templateId;
      state.serverResourceVersion = resourceVersion;
      state.serverDefinition = definition;
      state.serverLatestPublishedVersion = latestPublishedVersion;
      state.serverArchived = archived;
      state.serverInUse = inUse;
    },
    SET_SERVER_RESOURCE_VERSION(state, version) {
      state.serverResourceVersion = version;
    },
    SET_SERVER_DEFINITION(state, definition) {
      state.serverDefinition = definition;
    },
    RESET_ID_MAPS(state) {
      state.localToServerSectionIds = {};
      state.localToServerQuestionIds = {};
    },
    MAP_SECTION_ID(state, { localId, serverId }) {
      state.localToServerSectionIds = { ...state.localToServerSectionIds, [localId]: serverId };
    },
    MAP_QUESTION_ID(state, { localId, serverId }) {
      state.localToServerQuestionIds = { ...state.localToServerQuestionIds, [localId]: serverId };
    },
    SET_SAVE_STATUS(state, { status, message = '' }) {
      state.saveStatus = status || 'idle';
      state.saveErrorMessage = message || '';
    },
    SET_VALIDATE_STATUS(state, status) {
      state.validateStatus = status || 'idle';
    },
    SET_VALIDATION_RESULT(state, { valid, errors = [] }) {
      state.validateStatus = valid ? 'valid' : 'invalid';
      state.validationErrors = errors;
    },
    SET_PUBLISH_STATUS(state, { status, message = '' }) {
      state.publishStatus = status || 'idle';
      state.publishErrorMessage = message || '';
    },
  },

  actions: {
    updateBuilderDraft({ commit }, definition) {
      commit("SET_BUILDER_DRAFT", definition);
      commit("MARK_BUILDER_DIRTY", true);
    },
    resetBuilder({ commit }) {
      commit("RESET_BUILDER");
    },

    // createOrLoadTemplate creates the template on first save (the builder
    // starts with a purely local draft and no server id). If a template
    // already exists it is a no-op: saveTemplate is responsible for
    // reconciling further changes.
    async createOrLoadTemplate({ commit, state }, { title, description, api }) {
      if (state.serverTemplateId) return state.serverTemplateId;
      const response = await api.createTemplate({ title, description });
      const body = response.data;
      commit("SET_SERVER_TEMPLATE", {
        templateId: body.id,
        resourceVersion: body.resourceVersion,
        definition: toLocalDefinition(body.draftDefinition),
        latestPublishedVersion: body.latestPublishedVersion || 0,
        archived: body.archived || false,
        inUse: body.inUse || false,
      });
      return body.id;
    },

    // loadTemplate hydrates the builder from an EXISTING template (the
    // "Abrir" action from the template list). Unlike createOrLoadTemplate,
    // the definition fetched here already carries server-assigned
    // sectionId/questionId values, so the local->server id maps are seeded
    // as identity maps: without this, saveTemplate's diff (templateSync.js)
    // would treat every already-persisted section/question as brand new
    // (no id map entry) and re-create them as duplicates on first save.
    async loadTemplate({ commit }, { api, templateId }) {
      const response = await api.getTemplate(templateId);
      const body = response.data;
      const definition = toLocalDefinition(body.draftDefinition);
      commit("SET_SERVER_TEMPLATE", {
        templateId: body.id,
        resourceVersion: body.resourceVersion,
        definition,
        latestPublishedVersion: body.latestPublishedVersion || 0,
        archived: body.archived || false,
        inUse: body.inUse || false,
      });
      commit("SET_BUILDER_DRAFT", definition);
      commit("MARK_BUILDER_DIRTY", false);
      commit("RESET_ID_MAPS");
      (definition.sections || []).forEach((section) => {
        commit("MAP_SECTION_ID", { localId: section.sectionId, serverId: section.sectionId });
        (section.questions || []).forEach((question) => {
          commit("MAP_QUESTION_ID", { localId: question.questionId, serverId: question.questionId });
        });
      });
      return body.id;
    },

    // validateTemplate runs the backend's publish dry-run (no version is
    // created) against the last-saved draft, surfacing per-question errors
    // before the user attempts to actually publish.
    async validateTemplate({ commit, state }, { api }) {
      if (!state.serverTemplateId) return { valid: false, errors: [] };
      commit("SET_VALIDATE_STATUS", "validating");
      try {
        const response = await api.validateTemplate(state.serverTemplateId);
        commit("SET_VALIDATION_RESULT", response.data);
        return response.data;
      } catch (error) {
        commit("SET_VALIDATE_STATUS", "error");
        throw error;
      }
    },

    // publishTemplate creates the next immutable Versión_de_Plantilla. On
    // success it re-fetches the template (loadTemplate) instead of
    // hand-patching resourceVersion/latestPublishedVersion locally: publish
    // is a backend-owned mutation on the template document itself (not just
    // the new version), so re-syncing is simpler and safer than guessing
    // which fields it touched.
    async publishTemplate({ commit, dispatch, state }, { api }) {
      if (!state.serverTemplateId) throw new Error("No hay plantilla guardada para publicar.");
      commit("SET_PUBLISH_STATUS", { status: "publishing" });
      try {
        const response = await api.publishTemplate(state.serverTemplateId);
        commit("SET_PUBLISH_STATUS", { status: "published" });
        await dispatch("loadTemplate", { api, templateId: state.serverTemplateId });
        return response.data;
      } catch (error) {
        const isConflict = error && error.response && error.response.status === 409;
        const isInvalid = error && error.response && error.response.status === 422;
        const message = isConflict
          ? "La plantilla fue modificada por otra solicitud; recargue e intente de nuevo."
          : isInvalid
            ? "La plantilla no cumple los requisitos para publicarse. Revise los errores de validación."
            : "No se pudo publicar la plantilla. Intente nuevamente.";
        commit("SET_PUBLISH_STATUS", { status: "error", message });
        throw error;
      }
    },

    // saveTemplate diffs state.serverDefinition against state.builderDraft
    // and applies the resulting structural ops sequentially against the
    // backend, using the local id -> server id maps to keep the diff aware
    // of entities the server has already assigned an id to. On a 409
    // (TERRAIN_STALE_RESOURCE) it aborts immediately without applying any
    // further pending operation, since expectedVersion is now stale for
    // the whole batch.
    async saveTemplate({ commit, state, dispatch }, { api, title, description }) {
      commit("SET_SAVE_STATUS", { status: 'saving' });
      try {
        if (!state.serverTemplateId) {
          await dispatch("createOrLoadTemplate", { title, description, api });
        }

        const templateId = state.serverTemplateId;
        let expectedVersion = state.serverResourceVersion;
        const clearOps = diffConditionClearOps(
          state.serverDefinition, state.builderDraft,
          state.localToServerSectionIds, state.localToServerQuestionIds,
        );
        const ops = diffDefinitionsToOps(
          state.serverDefinition,
          state.builderDraft,
          state.localToServerSectionIds,
          state.localToServerQuestionIds,
        );

        let lastResponseDefinition = state.serverDefinition;

        for (const op of [...clearOps, ...ops]) {
          const { __localSectionId, __localQuestionId, __pendingQuestions, ...request } = op;
          request.expectedVersion = expectedVersion;

          const response = await api.updateTemplateStructure(templateId, request);
          const body = response.data;
          expectedVersion = body.resourceVersion;
          lastResponseDefinition = toLocalDefinition(body.draftDefinition);
          commit("SET_SERVER_RESOURCE_VERSION", expectedVersion);

          if (op.op === 'addSection' && __localSectionId) {
            const newSection = lastResponseDefinition.sections[lastResponseDefinition.sections.length - 1];
            if (newSection) commit("MAP_SECTION_ID", { localId: __localSectionId, serverId: newSection.sectionId });

            // Newly created sections have no server id yet for their
            // questions either: add them now, one addQuestion per pending
            // question, reusing the same expectedVersion chain.
            for (const pendingQuestion of __pendingQuestions || []) {
              const { questionId: localQuestionId } = pendingQuestion;
              const questionBody = prepareQuestionForAdd(pendingQuestion);
              const questionResponse = await api.updateTemplateStructure(templateId, {
                op: 'addQuestion',
                expectedVersion,
                sectionId: newSection.sectionId,
                question: questionBody,
              });
              const questionBodyResp = questionResponse.data;
              expectedVersion = questionBodyResp.resourceVersion;
              lastResponseDefinition = toLocalDefinition(questionBodyResp.draftDefinition);
              commit("SET_SERVER_RESOURCE_VERSION", expectedVersion);
              const updatedServerSection = lastResponseDefinition.sections
                .find((section) => section.sectionId === newSection.sectionId);
              const newQuestion = updatedServerSection && updatedServerSection.questions[updatedServerSection.questions.length - 1];
              if (newQuestion) commit("MAP_QUESTION_ID", { localId: localQuestionId, serverId: newQuestion.questionId });
            }
          }

          if (op.op === 'addQuestion' && __localQuestionId) {
            const updatedServerSection = lastResponseDefinition.sections
              .find((section) => section.sectionId === request.sectionId);
            const newQuestion = updatedServerSection && updatedServerSection.questions[updatedServerSection.questions.length - 1];
            if (newQuestion) commit("MAP_QUESTION_ID", { localId: __localQuestionId, serverId: newQuestion.questionId });
          }
        }

        const conditionOps = diffConditionOps(
          lastResponseDefinition, state.builderDraft,
          state.localToServerSectionIds, state.localToServerQuestionIds,
        );
        for (const op of conditionOps) {
          const response = await api.updateTemplateStructure(templateId, { ...op, expectedVersion });
          expectedVersion = response.data.resourceVersion;
          lastResponseDefinition = toLocalDefinition(response.data.draftDefinition);
          commit("SET_SERVER_RESOURCE_VERSION", expectedVersion);
        }

        commit("SET_SERVER_DEFINITION", lastResponseDefinition);
        commit("SET_BUILDER_DRAFT", lastResponseDefinition);
        commit("RESET_ID_MAPS");
        (lastResponseDefinition.sections || []).forEach((section) => {
          commit("MAP_SECTION_ID", { localId: section.sectionId, serverId: section.sectionId });
          (section.questions || []).forEach((question) => commit("MAP_QUESTION_ID", { localId: question.questionId, serverId: question.questionId }));
        });
        commit("MARK_BUILDER_DIRTY", false);
        commit("SET_SAVE_STATUS", { status: 'saved' });
      } catch (error) {
        const isConflict = error && error.response && error.response.status === 409;
        const message = isConflict
          ? "La plantilla fue modificada por otra solicitud; recargue e intente de nuevo."
          : "No se pudo guardar la plantilla. Intente nuevamente.";
        commit("SET_SAVE_STATUS", { status: 'error', message });
        throw error;
      }
    },
  },
};
